// The brand bar and the theme control, for an app that is not React.
//
// Hand-written, not generated: this is logic, like theme.js.
//
// WHY THIS EXISTS. The kit had a reference implementation for React apps and
// none for plain ones. luxengine.com and LUXplan both read the tagline out of
// BRANDS, both cycle system/light/dark on the shared storage key, and both
// therefore agree. A vanilla app following the README got `tokens.css` and a
// `data-brand` attribute, and had to reinvent the rest — so LuxLab retyped the
// tagline, invented a two-mode toggle on its own storage key, and shipped a
// brand switcher. None of that was a mistake about the brand; it was the kit
// having no answer for the question. This file is the answer.
//
// Dependency-free ES module, same as theme.js, so a static page can take it
// with a <script type="module"> and no build step.

import { BRANDS } from './brands.js';
import { getMode, initTheme, nextMode, resolveMode, setMode } from './theme.js';

/**
 * The brand block: lockup, and the brand's tagline under it.
 *
 * BOTH lockups are rendered and the scheme hides one, which is what
 * components.css's `bk-on-*-only` pair is for. Branching in JS after mount
 * would show the wrong mark for a frame on every load.
 *
 * The RASTER lockup is used wherever the brand has one, because that is what
 * luxengine.com and LUXplan both render. WebKit rasterises SVG `<filter>`
 * output at a fixed resolution and then scales it, and this artwork is
 * entirely filter-driven including a luminance-to-alpha mask, so at the sizes
 * a header draws it the crosshair's thin gold ring picked up dark fringing.
 * A PNG has no filter to resample. Falling back to the SVG keeps a brand that
 * ships no raster working.
 *
 * The tagline comes from the kit and is never passed in. It is wording, not
 * artwork — luxengine.com sets it as text under the mark — so an app that
 * types its own is guessing at both the words and the tracking. If an app
 * wants to say what IT is, that is a heading in the page, not this slot.
 *
 * @param {string} brandId
 * @param {{ assetBase?: string, withTagline?: boolean }} [options]
 *   `assetBase` is where the app serves the kit's `assets/` from; the kit
 *   cannot know, because every app vendors it to a different path.
 */
export function brandbarBrandHTML(brandId, options = {}) {
  const brand = resolveBrandOrThrow(brandId);
  const base = (options.assetBase ?? 'assets').replace(/\/$/, '');
  const withTagline = options.withTagline ?? true;
  const primary = brand.logos.primary;
  const src = (p) => `${base}/${String(p).replace(/^assets\//, '')}`;
  const alt = escapeAttr(brand.displayName);
  const onLight = primary.onLightRaster ?? primary.onLight;
  const onDark = primary.onDarkRaster ?? primary.onDark;

  const tagline =
    withTagline && brand.tagline
      ? `\n  <span class="bk-brandbar__tagline">${escapeText(brand.tagline)}</span>`
      : '';

  return (
    `<img class="bk-brandbar__mark bk-on-light-only" src="${escapeAttr(src(onLight))}" alt="${alt}" />\n` +
    `  <img class="bk-brandbar__mark bk-on-dark-only" src="${escapeAttr(src(onDark))}" alt="${alt}" />` +
    tagline
  );
}

/**
 * Render the brand block into a container, replacing whatever is there.
 *
 * Replacing rather than appending is deliberate: the markup this overwrites is
 * usually a hand-typed lockup, and leaving it in place would mean the page
 * renders correctly only until someone edits the copy it still contains.
 *
 * @param {Element} el  the `.bk-brandbar__brand` container
 */
export function mountBrandbar(el, brandId, options = {}) {
  if (!el) return;
  el.classList.add('bk-brandbar__brand');
  el.innerHTML = brandbarBrandHTML(brandId, options);
  return el;
}

/**
 * The favicon block, exactly as luxengine.com serves it.
 *
 * This is markup rather than a copied snippet because the SHAPE of it is the
 * fix, and the shape looks arbitrary. It was settled on 2026-08-14 after a
 * morning of reverts against real iPads, and every decision in it is invisible
 * on desktop:
 *
 *   NO .ico, ever. iOS prefers it over the SVG and the PNGs, and the ICO
 *   container's separate 1-bit transparency mask does not survive, so iOS
 *   treats the icon as opaque and (0,0,0,0) renders as a BLACK BOX behind the
 *   reticle. Proven by serving each candidate in a different colour on an iPad
 *   and seeing which one it picked. There is no parameter to add one back.
 *
 *   apple-touch-icon must be TRANSPARENT. It is the next thing iOS falls back
 *   to, and ours was a black square; making it cream only changed the
 *   complaint.
 *
 *   PNGs descend 48 -> 32 -> 16. 48 is what retina tab strips take.
 *
 * And the rule that outlives this function: to change an icon, change the
 * FILENAME. iOS keeps its own icon database keyed on URL and ignores
 * cache-control, so overwriting in place leaves every device that saw the old
 * one showing it forever.
 *
 * @param {string} brandId
 * @param {{ assetBase?: string }} [options]
 * @returns {string} link tags, or '' for a brand with no icon set
 */
export function faviconHTML(brandId, options = {}) {
  const brand = resolveBrandOrThrow(brandId);
  const icons = brand.icons;
  if (!icons) return '';
  const base = (options.assetBase ?? 'assets').replace(/\/$/, '');
  const href = (p) => escapeAttr(`${base}/${String(p).replace(/^assets\//, '')}`);

  // Emit only what the brand actually declares. A brand may reasonably have a
  // vector mark and no raster set yet, and the two failure modes otherwise are
  // both silent-ish: a missing `png` throws a TypeError out of Object.keys,
  // and a missing `svg` writes href=".../undefined" — a link tag that looks
  // fine in the markup and 404s in the browser.
  const out = [];
  if (icons.svg) {
    out.push(`<link rel="icon" type="image/svg+xml" href="${href(icons.svg)}" />`);
  }
  for (const size of Object.keys(icons.png ?? {}).sort((a, b) => Number(b) - Number(a))) {
    out.push(
      `<link rel="icon" type="image/png" sizes="${size}x${size}" href="${href(icons.png[size])}" />`,
    );
  }
  if (icons.appleTouch) {
    out.push(`<link rel="apple-touch-icon" sizes="180x180" href="${href(icons.appleTouch)}" />`);
  }
  return out.join('\n');
}

/** The three icons luxengine.com and LUXplan carry, so the control looks the
 *  same on every surface and not merely behave the same. */
const ICONS = {
  system:
    '<circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 3" />',
  light:
    '<circle cx="12" cy="12" r="4" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M19.1 4.9L17 7M7 17l-2.1 2.1" />',
  dark: '<path d="M21 12.8A9 9 0 1111.2 3a7 7 0 009.8 9.8z" />',
};

function iconSVG(mode) {
  return (
    '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    `stroke-width="1.8" aria-hidden="true">${ICONS[mode]}</svg>`
  );
}

function labelFor(mode) {
  if (mode === 'system') return `Following your system (${resolveMode('system')})`;
  return mode === 'light' ? 'Light theme' : 'Dark theme';
}

/** luxengine.com says "click to change"; LUXplan says "tap", because it is an
 *  iPad app. The verb is the only thing that differs between them, so it is a
 *  parameter rather than a second copy of the control. */
const DEFAULT_VERB = 'click';

/**
 * Wire a button as the shared theme control.
 *
 * Three modes cycled by one press — system, light, dark — on the kit's storage
 * key, so a visitor who set dark on luxengine.com arrives here already dark.
 *
 * The button is labelled with an ICON and an accessible name describing the
 * CURRENT state plus how to change it. A text label naming the current scheme
 * is the trap worth naming: a button reading "LIGHT" while the page is light
 * looks like a button that makes the page light, and pressing it does the
 * opposite of what it says.
 *
 * @param {HTMLButtonElement} button
 * @returns {() => void} unsubscribe, safe to call twice
 */
export function mountThemeToggle(button, options = {}) {
  if (!button) return () => {};
  let mode = options.mode ?? getMode(options.defaultMode ?? 'system');
  let stop = () => {};

  const verb = options.verb ?? DEFAULT_VERB;
  const paint = () => {
    const label = `${labelFor(mode)} — ${verb} to change`;
    button.innerHTML = iconSVG(mode);
    button.title = label;
    button.setAttribute('aria-label', label);
  };

  const run = () => {
    stop();
    // initTheme repaints on an OS change while the mode is 'system', and the
    // button's own label has to follow it — 'Following your system (dark)'
    // goes stale otherwise.
    stop = initTheme({ ...options, mode, onChange: () => paint() });
    paint();
  };

  const onClick = () => {
    mode = nextMode(mode);
    setMode(mode);
    run();
  };

  button.type = 'button';
  // The kit's own styling, so an app cannot be left guessing at it. Additive:
  // an app that already has a class on the button keeps it.
  button.classList.add('bk-theme-toggle');
  button.addEventListener('click', onClick);

  run();
  // Detaches the listener as well as the media subscription. Returning only
  // initTheme's unsubscribe would leave a live button behind: a caller that
  // tears down its header and mounts a fresh control on the same node would
  // then have two, both writing data-theme and the shared storage key.
  return () => {
    button.removeEventListener('click', onClick);
    stop();
  };
}

function resolveBrandOrThrow(brandId) {
  const brand = BRANDS[brandId];
  // Louder than falling back to BWRM. A static page names its brand as a
  // literal, so a wrong one is a typo that should fail at once rather than
  // render the other company's palette and wait to be noticed.
  if (!brand) {
    throw new Error(
      `Unknown brand ${JSON.stringify(brandId)}. Known: ${Object.keys(BRANDS).join(', ')}`,
    );
  }
  return brand;
}

const ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
function escapeText(s) {
  return String(s).replace(/[&<>]/g, (c) => ESCAPES[c]);
}
function escapeAttr(s) {
  return String(s).replace(/[&<>"']/g, (c) => ESCAPES[c]);
}
